/* =============================================================================
   SMOKEY'S DELIVERY MINIMUM — shared storefront policy
   =============================================================================
   One evaluator is used by the eager checkout fence and cart-delivery.js.

   Rules:
     * applies only when the cart contains a complete delivery selection
     * discounted merchandise counts (final_line_price)
     * Express fee variants never count
     * tax, tip, shipping and other checkout charges never count
     * pickup is never spend-gated
     * the existing distance-tier minimum remains authoritative

   This policy stays in the existing theme because custom-app Shopify Functions
   require Plus. Every checkout surface rendered by this theme uses the eager
   fence below; ambiguous state remains fail-open to protect legitimate orders.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__smokeysDeliveryMinimumInstalled) return;
  window.__smokeysDeliveryMinimumInstalled = true;

  var latestCountdownMinimumCents = 0;
  var refusalDedupe = Object.create(null);
  var REFUSAL_DEDUPE_MS = 30000;
  var REFUSAL_ENDPOINT = '/apps/purp-delivery/delivery-promise/eligibility-refusal';
  var REFUSAL_CONTRACT = 'shopify_delivery_eligibility_refusal_v1';

  function positiveCents(value) {
    var cents = Number(value);
    return Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
  }

  function collectIds(source, target) {
    if (!source || typeof source !== 'object') return;
    Object.keys(source).forEach(function (key) {
      var value = source[key];
      if (value && typeof value === 'object') {
        collectIds(value, target);
        return;
      }
      if (value !== null && value !== undefined && value !== '') {
        target[String(value)] = true;
      }
    });
  }

  function expressVariantIds() {
    var ids = Object.create(null);
    collectIds(window.EXPRESS_VARIANTS, ids);
    collectIds(window.EXPRESS_VARIANTS_BY_STORE, ids);
    return ids;
  }

  function isExpressItem(item) {
    if (!item) return false;
    var id = item.variant_id !== undefined ? item.variant_id : item.variantId;
    return !!expressVariantIds()[String(id || '')];
  }

  function realItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(function (item) { return !isExpressItem(item); });
  }

  function merchandiseSubtotalCents(cart) {
    return realItems(cart).reduce(function (sum, item) {
      return sum + itemTotalCents(item);
    }, 0);
  }

  function itemTotalCents(item) {
    if (!item) return 0;
    var candidates = [item.final_line_price, item.line_price];
    for (var i = 0; i < candidates.length; i += 1) {
      if (candidates[i] !== null && candidates[i] !== undefined && candidates[i] !== '' &&
          Number.isFinite(Number(candidates[i]))) {
        return Math.max(0, Math.round(Number(candidates[i])));
      }
    }
    var unit = Number(item.final_price);
    var quantity = Number(item.quantity);
    if (Number.isFinite(unit) && Number.isFinite(quantity)) {
      return Math.max(0, Math.round(unit * quantity));
    }
    return 0;
  }

  function deliveryIntent(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return { proven: false, kind: 'unavailable' };
    }

    if (cart.items.some(isExpressItem)) {
      return { proven: true, kind: 'express_product' };
    }

    var attrs = cart.attributes || {};
    var hasWindow = String(attrs['Delivery Window'] || '').trim() !== '';
    var hasSlot = String(attrs._delivery_slot_id || '').trim() !== '';
    if (hasWindow && hasSlot) {
      return { proven: true, kind: 'scheduled_window' };
    }

    // A lone customer-editable/stale delivery attribute is not enough evidence
    // to block checkout. cart-delivery.js repairs incomplete selections.
    return { proven: false, kind: 'none' };
  }

  function resolveMinimumCents(override) {
    try {
      var quote = window.SMOKEYS_DELIVERY && window.SMOKEYS_DELIVERY.getQuote &&
        window.SMOKEYS_DELIVERY.getQuote();
      var quoted = positiveCents(quote && quote.minOrderCents);
      if (quoted) return quoted;
    } catch (e) {}

    // cart-delivery may pass the last countdown result while a new quote is
    // loading. It is fallback evidence only and must never override the
    // current canonical quote.
    var overrideCents = positiveCents(override);
    if (overrideCents) return overrideCents;

    if (latestCountdownMinimumCents) return latestCountdownMinimumCents;

    // Never invent a delivery minimum. If neither the canonical live quote nor
    // the countdown brain has a current value, fail open so a legitimate
    // checkout is not stranded.
    return null;
  }

  function getEligibility(cart, minimumOverride) {
    var items = realItems(cart);
    var merchandiseSubtotal = merchandiseSubtotalCents(cart);
    var minimumRequired = resolveMinimumCents(minimumOverride);
    var minimumKnown = Number.isFinite(minimumRequired) && minimumRequired > 0;
    var hasRealProducts = items.length > 0;
    var remainingToMinimum = minimumKnown
      ? Math.max(0, minimumRequired - merchandiseSubtotal)
      : 0;

    return {
      hasRealProducts: hasRealProducts,
      merchandiseSubtotal: merchandiseSubtotal,
      minimumRequired: minimumRequired,
      minimumKnown: minimumKnown,
      remainingToMinimum: remainingToMinimum,
      meetsDeliveryMinimum: !minimumKnown || (hasRealProducts && merchandiseSubtotal >= minimumRequired)
    };
  }

  function evaluateCheckout(cart, minimumOverride) {
    var intent = deliveryIntent(cart);
    var eligibility = getEligibility(cart, minimumOverride);
    return Object.assign({}, eligibility, {
      applicable: intent.proven,
      deliveryKind: intent.kind,
      allowed: !intent.proven || !eligibility.minimumKnown || eligibility.meetsDeliveryMinimum
    });
  }

  function messageFor(result) {
    if (!result || !result.applicable || result.allowed) return '';
    if (!result.hasRealProducts) {
      return 'Add products to your cart before choosing delivery checkout.';
    }
    return 'Add $' + (result.remainingToMinimum / 100).toFixed(2) +
      ' more in products for delivery. The Express fee does not count toward the $' +
      (result.minimumRequired / 100).toFixed(2) + ' minimum, or choose pickup.';
  }

  function telemetryStoreId() {
    try {
      var identity = window.SmokeysLocation;
      if (!identity || typeof identity.currentId !== 'function') return '';
      var seed = window.__SMOKEYS_SERVER_STORE_SEED || window.storeId || '';
      var canonical = identity.currentId(seed);
      if (!canonical) return '';
      var legacy = typeof identity.legacyConsumerId === 'function'
        ? identity.legacyConsumerId(canonical)
        : '';
      var value = String(legacy || canonical).trim();
      return value.length <= 256 ? value : '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Advisory telemetry only. It never returns a promise, changes storefront
   * state, or exposes customer/cart/location details. Transport failures are
   * swallowed so PURP can never become a checkout dependency.
   */
  function reportMinimumRefusal(input) {
    try {
      input = input || {};
      var eligibility = input.eligibility || {};
      var minimumCents = positiveCents(eligibility.minimumRequired);
      var subtotalCents = Math.max(0, Math.round(Number(eligibility.merchandiseSubtotal)));
      if (!minimumCents || !Number.isFinite(subtotalCents) || subtotalCents >= minimumCents) return;

      var decisionPoint = input.decisionPoint === 'checkout_attempt'
        ? 'checkout_attempt'
        : 'minimum_notice';
      var surfaces = {
        cart: true,
        cart_drawer: true,
        cart_notification: true,
        checkout_link: true,
        accelerated_checkout: true
      };
      var surface = surfaces[input.surface] ? input.surface : 'cart';
      var deliveryKinds = {
        scheduled_window: true,
        express_product: true,
        unknown: true
      };
      var deliveryKind = deliveryKinds[input.deliveryKind] ? input.deliveryKind : 'unknown';
      var storeId = telemetryStoreId();
      var payload = {
        contract: REFUSAL_CONTRACT,
        reason: 'minimum_spend',
        decisionPoint: decisionPoint,
        surface: surface,
        deliveryKind: deliveryKind,
        minimumCents: minimumCents,
        merchandiseSubtotalCents: subtotalCents
      };
      if (storeId) payload.storeId = storeId;

      var key = [decisionPoint, surface, deliveryKind, minimumCents, subtotalCents, storeId].join('|');
      var now = Date.now();
      if (refusalDedupe[key] && now - refusalDedupe[key] < REFUSAL_DEDUPE_MS) return;
      refusalDedupe[key] = now;

      var request = window.fetch(REFUSAL_ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (request && typeof request.catch === 'function') request.catch(function () {});
    } catch (e) {}
  }

  window.addEventListener('deliveryEligibilityChanged', function (event) {
    var detail = event && event.detail;
    if (!detail || detail.pickupMode) {
      latestCountdownMinimumCents = 0;
      return;
    }
    latestCountdownMinimumCents = positiveCents(detail.minimumCents);
  });
  ['storeSelected', 'deliveryModeChanged', 'userPositionChanged'].forEach(function (eventName) {
    window.addEventListener(eventName, function () {
      latestCountdownMinimumCents = 0;
    });
  });

  window.SmokeysDeliveryMinimum = Object.freeze({
    deliveryIntent: deliveryIntent,
    evaluateCheckout: evaluateCheckout,
    expressVariantIds: expressVariantIds,
    getEligibility: getEligibility,
    isExpressItem: isExpressItem,
    messageFor: messageFor,
    merchandiseSubtotalCents: merchandiseSubtotalCents,
    realItems: realItems,
    reportMinimumRefusal: reportMinimumRefusal,
    resolveMinimumCents: resolveMinimumCents
  });
})();
